import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './AuthContext';
import { AdminPage } from './AdminPage';
import { PublicLayout } from './PublicLayout';
import {
  CompleteGoogleRegistrationPage,
  ForgotPasswordPage,
  LoginPage,
  RegisterPage,
  ResetPasswordPage,
} from './pages';
import {
  PublicHomePage,
  PricingPage,
  CheckoutPage,
  CheckoutSuccessPage,
  WatZijnSurebetsPage,
  PersonalTrackerPage,
  BookmakersPage,
  MarktenPage,
  KennisbankPage,
  FAQPage,
  ContactPage,
  CalculatorPage,
  TelegramPage,
  RiscosVanSurebettingPage,
  MijnAccountPage,
  GeenToegangPage,
  AlgemeneVoorwaardenPage,
  PrivacybeleidPage,
} from './PublicPages';
import {
  DashboardPage,
  TrackerMainPage,
} from './TrackerWorkspacePages';
import { TrackerHistoryPage } from './TrackerHistoryPage';
import { useAuth } from './AuthContext';

const IS_SANDBOX = process.env.REACT_APP_SANDBOX_MODE === 'true';

const AppLoadingFallback: React.FC = () => (
  <PublicLayout>
    <section className="public-page-card subscription-page">
      <section className="subscription-hero">
        <div className="site-loading-block site-loading-block-centered" role="status" aria-live="polite">
          <span className="site-loading-spinner" aria-hidden="true" />
          <p>Accountgegevens worden geladen...</p>
        </div>
      </section>
    </section>
  </PublicLayout>
);

const RequireLogin: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { user, isLoading, isLoggedIn } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <AppLoadingFallback />;
  }

  if (!isLoggedIn() || !user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return children;
};

const RequireTrackerAccess: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { canAccessTracker, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <AppLoadingFallback />;
  }

  if (!canAccessTracker()) {
    return <Navigate to="/geen-toegang" replace state={{ reason: 'tracker', from: location.pathname }} />;
  }

  return children;
};

const RequireAdminAccess: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { canAccessAdmin, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <AppLoadingFallback />;
  }

  if (!canAccessAdmin()) {
    return <Navigate to="/geen-toegang" replace state={{ reason: 'admin', from: location.pathname }} />;
  }

  return children;
};

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        {IS_SANDBOX && (
          <div className="sandbox-mode-banner" role="status">
            <strong>Stripe Sandbox</strong>
            <span>Testomgeving · er wordt geen echt geld afgeschreven</span>
          </div>
        )}
        <Routes>
          {/* Publiek */}
          <Route path="/" element={<PublicHomePage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          <Route path="/surebets" element={<WatZijnSurebetsPage />} />
          <Route path="/bookmaker-limieten" element={<Navigate to="/bookmakers" replace />} />
          <Route path="/calculator" element={<CalculatorPage />} />
          <Route path="/tracker-uitleg" element={<PersonalTrackerPage />} />
          <Route path="/bookmakers" element={<BookmakersPage />} />
          <Route path="/markten" element={<MarktenPage />} />
          <Route path="/veelgestelde-vragen" element={<FAQPage />} />
          <Route path="/faq" element={<Navigate to="/veelgestelde-vragen" replace />} />
          <Route path="/kennisbank" element={<KennisbankPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/algemene-voorwaarden" element={<AlgemeneVoorwaardenPage />} />
          <Route path="/privacybeleid" element={<PrivacybeleidPage />} />
          
          {/* Telegram */}
          <Route path="/telegram" element={<TelegramPage />} />
          <Route path="/mijn-account" element={<RequireLogin><MijnAccountPage /></RequireLogin>} />
          <Route path="/geen-toegang" element={<GeenToegangPage />} />
          <Route path="/admin" element={<RequireAdminAccess><AdminPage /></RequireAdminAccess>} />

          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/registreren/afronden" element={<CompleteGoogleRegistrationPage />} />
          <Route path="/wachtwoord-vergeten" element={<ForgotPasswordPage />} />
          <Route path="/wachtwoord-resetten" element={<ResetPasswordPage />} />
          
          {/* Kennisbank */}
          <Route path="/kennisbank/risicos-surebetten" element={<RiscosVanSurebettingPage />} />

          {/* Ingelogd */}
          <Route path="/dashboard" element={<RequireLogin><DashboardPage /></RequireLogin>} />
          <Route path="/tracker" element={<RequireTrackerAccess><TrackerMainPage /></RequireTrackerAccess>} />
          <Route path="/tracker/surebet-history" element={<TrackerHistoryPage />} />

          {/* Compatibiliteit redirects */}
          <Route path="/personal-tracker" element={<Navigate to="/tracker-uitleg" replace />} />
          <Route path="/hoe-het-werkt" element={<Navigate to="/surebets" replace />} />
          <Route path="/voorbeeldmelding" element={<Navigate to="/" replace />} />
          <Route path="/abonnementen/gratis-groep" element={<Navigate to="/pricing" replace />} />
          <Route path="/abonnementen/pro" element={<Navigate to="/pricing" replace />} />
          <Route path="/abonnementen/pre-match" element={<Navigate to="/pricing" replace />} />
          <Route path="/abonnementen/live" element={<Navigate to="/pricing" replace />} />

          <Route path="/surebet-history" element={<Navigate to="/tracker/surebet-history" replace />} />
          <Route path="/results" element={<Navigate to="/tracker" replace />} />
          <Route path="/payments" element={<Navigate to="/tracker" replace />} />
          <Route path="/tracker/results" element={<Navigate to="/tracker" replace />} />
          <Route path="/tracker/payments" element={<Navigate to="/tracker" replace />} />
          <Route path="/tracker/bets" element={<Navigate to="/tracker" replace />} />

          
          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
